/**
 * ProspectEditViewer Component
 * Edit mode view for editing prospect profile and about information
 */

import { ProfileDetailsForm } from "./edit/ProfileDetailsForm";
import type {
  ProfileFormData,
  FormErrors,
} from "./edit/ProfileDetailsForm";
import { AboutDetailsForm } from "./edit/AboutDetailsForm";
import type { AboutFormData } from "./edit/AboutDetailsForm";
import { TrashIcon } from "../../../../assets/TrashIcon";

interface ProspectEditViewerProps {
  activeEditTab: "profile" | "about";
  onEditTabChange: (tab: "profile" | "about") => void;
  profileFormData: ProfileFormData;
  aboutFormData: AboutFormData;
  formErrors: FormErrors;
  onProfileFormChange: (data: Partial<ProfileFormData>) => void;
  onAboutFormChange: (data: Partial<AboutFormData>) => void;
  onClearFormError: (field: keyof FormErrors) => void;
  isSaving: boolean;
  saveError: string | null;
  onSave: () => void;
  onCancel: () => void;
  onDeleteClick: () => void;
}

export function ProspectEditViewer({
  activeEditTab,
  onEditTabChange,
  profileFormData,
  aboutFormData,
  formErrors,
  onProfileFormChange,
  onAboutFormChange,
  onClearFormError,
  isSaving,
  saveError,
  onSave,
  onCancel,
  onDeleteClick,
}: ProspectEditViewerProps) {
  return (
    <>
      {/* Edit Header */}
      <div className="prospect-viewer-panel__header">
        <h2 className="prospect-viewer-panel__title">Edit Profile</h2>
        <div className="prospect-viewer-panel__header-buttons">
          <button
            className="prospect-viewer-panel__edit-save-button"
            onClick={onSave}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save"}
          </button>
          <button
            className="prospect-viewer-panel__edit-cancel-button"
            onClick={onCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
        </div>
      </div>

      {/* Edit Tabs */}
      <div className="prospect-viewer-panel__sub-tabs">
        <div className="prospect-viewer-panel__sub-tabs-left">
          <button
            className={`prospect-viewer-panel__sub-tab ${
              activeEditTab === "profile"
                ? "prospect-viewer-panel__sub-tab--active"
                : ""
            }`}
            onClick={() => onEditTabChange("profile")}
          >
            Profile
          </button>
          <button
            className={`prospect-viewer-panel__sub-tab ${
              activeEditTab === "about"
                ? "prospect-viewer-panel__sub-tab--active"
                : ""
            }`}
            onClick={() => onEditTabChange("about")}
          >
            About
          </button>
        </div>
        <button
          className="prospect-viewer-panel__delete-button-icon"
          onClick={onDeleteClick}
          disabled={isSaving}
          aria-label="Delete prospect"
        >
          <TrashIcon />
        </button>
      </div>

      {/* Edit Form */}
      <div className="prospect-viewer-panel__edit-form">
        {activeEditTab === "profile" && (
          <ProfileDetailsForm
            formData={profileFormData}
            formErrors={formErrors}
            onFormChange={onProfileFormChange}
            onClearError={onClearFormError}
            disabled={isSaving}
          />
        )}
        {activeEditTab === "about" && (
          <AboutDetailsForm
            formData={aboutFormData}
            onFormChange={onAboutFormChange}
            disabled={isSaving}
          />
        )}
      </div>

      {saveError && (
        <div className="prospect-viewer-panel__error">{saveError}</div>
      )}
    </>
  );
}
